/**
 * Single source of truth for parsing monetary values.
 *
 * Used by the raw material import (both the pre-flight validator and the
 * importing function that POSTs), and by the paste guard on price inputs.
 * Keeping one implementation is what guarantees a value such as an em dash
 * behaves identically whether it is typed, pasted, or imported from Excel.
 */

export interface ParsedMonetary {
  /** The parsed amount, or null when the input could not be recovered. */
  value: number | null;
  /** Human-readable reason the value was rejected, or null when parsing succeeded. */
  error: string | null;
  /**
   * True when the raw input was not already a clean number and had to be
   * corrected (currency symbol, unicode dash, thousands separator, comma
   * decimal, stray whitespace). Callers surface these for user review.
   */
  sanitised: boolean;
  /** The original input rendered as a string, for error messages and review lists. */
  rawValue: string;
}

/**
 * Unicode characters that look like a minus/dash but are not the ASCII '-'.
 * Excel, Word and accounting formats produce these routinely; none of them
 * carry numeric content on their own.
 */
const DASH_CHARACTERS = /[‐‑‒–—―−﹘﹣－]/g;

/** Non-breaking, narrow and thin spaces used as thousands separators. */
const UNICODE_SPACES = /[  -​  　]/g;

/** Currency symbols and ISO codes we accept as decoration around a number. */
const CURRENCY_TOKENS = [
  'ZAR', 'USD', 'EUR', 'GBP', 'JPY', 'INR',
  'R', '$', '€', '£', '¥', '₹',
];

/**
 * Unwrap the object shapes ExcelJS hands back for non-plain cells.
 * Without this they stringify to "[object Object]" and every such cell is
 * rejected with a useless message.
 */
function unwrapCellValue(value: unknown): unknown {
  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value === 'object') {
    const cell = value as Record<string, any>;

    // Formula cell: { formula: '=A1*2', result: 20 }
    if ('result' in cell) {
      return cell['result'];
    }
    // Hyperlink cell: { text: '10.50', hyperlink: '...' }
    if ('text' in cell) {
      return cell['text'];
    }
    // Rich text cell: { richText: [{ text: '10' }, { text: '.50' }] }
    if (Array.isArray(cell['richText'])) {
      return cell['richText'].map((run: any) => run?.text ?? '').join('');
    }
    // Date cells are never valid prices.
    if (value instanceof Date) {
      return NaN;
    }
  }

  return value;
}

/**
 * Reject inputs that Number() happily accepts but a price column should not:
 * hex/binary/octal literals, exponent notation and Infinity. A price cell
 * containing "1e5" is far more likely to be a typo than an intended 100000.
 */
function isDisallowedNumericLiteral(text: string): boolean {
  return /^[+-]?0[xXbBoO]/.test(text)
    || /e/i.test(text)
    || /infinity/i.test(text);
}

export function parseMonetary(input: unknown): ParsedMonetary {
  const unwrapped = unwrapCellValue(input);

  // An object shape we do not recognise would stringify to "[object Object]",
  // which is meaningless in an error message shown to the user.
  if (unwrapped !== null && unwrapped !== undefined && typeof unwrapped === 'object') {
    return {
      value: null,
      error: 'Invalid price format: the cell does not contain a number',
      sanitised: false,
      rawValue: '',
    };
  }

  const rawValue = unwrapped === null || unwrapped === undefined ? '' : String(unwrapped);

  // A number that arrived as a number needs no sanitising.
  if (typeof unwrapped === 'number') {
    if (!Number.isFinite(unwrapped)) {
      return { value: null, error: 'Price is not a valid number', sanitised: false, rawValue };
    }
    if (unwrapped < 0) {
      return { value: null, error: 'Price cannot be negative', sanitised: false, rawValue };
    }
    return { value: unwrapped, error: null, sanitised: false, rawValue };
  }

  const trimmed = rawValue.trim();
  if (trimmed === '') {
    return { value: null, error: 'Price is required', sanitised: false, rawValue };
  }

  // Fast path: already a clean numeric string, no correction needed.
  if (!isDisallowedNumericLiteral(trimmed) && /^[+-]?\d+(\.\d+)?$/.test(trimmed)) {
    const direct = Number(trimmed);
    if (direct < 0) {
      return { value: null, error: 'Price cannot be negative', sanitised: false, rawValue };
    }
    return { value: direct, error: null, sanitised: false, rawValue };
  }

  let cleaned = trimmed;

  // Normalise unicode punctuation before anything else. An em dash left in
  // place survives currency stripping and silently poisons the value.
  cleaned = cleaned.replace(DASH_CHARACTERS, '-');
  cleaned = cleaned.replace(UNICODE_SPACES, ' ');

  // Strip currency decoration. Longest tokens first so 'ZAR' is not partly
  // consumed by the 'R' rule.
  for (const token of CURRENCY_TOKENS) {
    const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    cleaned = cleaned.replace(new RegExp(escaped, 'gi'), '');
  }

  cleaned = cleaned.trim();

  // A lone dash (from an em dash, en dash or accounting-style zero) carries no
  // numeric content. Reject it explicitly rather than letting it fall through.
  if (/^-+$/.test(cleaned)) {
    return {
      value: null,
      error: `Invalid price format: "${trimmed}". Please use numeric values only (e.g., 10.50)`,
      sanitised: false,
      rawValue,
    };
  }

  // Space thousands separators first ("1 234,56"), otherwise the comma-decimal
  // check below cannot match and the value is rejected outright.
  cleaned = cleaned.replace(/(\d)[ \t]+(\d)/g, '$1$2');

  // Comma used as a decimal separator: "1234,56"
  const commaDecimal = cleaned.match(/^([+-]?\d+),(\d{1,2})$/);
  if (commaDecimal) {
    cleaned = `${commaDecimal[1]}.${commaDecimal[2]}`;
  }

  // Comma thousands separators: "1,234,567.89"
  while (/(\d),(\d{3})(\D|$)/.test(cleaned)) {
    cleaned = cleaned.replace(/(\d),(\d{3})(\D|$)/g, '$1$2$3');
  }

  cleaned = cleaned.trim();

  if (cleaned === '' || isDisallowedNumericLiteral(cleaned) || !/^[+-]?\d+(\.\d+)?$/.test(cleaned)) {
    return {
      value: null,
      error: `Invalid price format: "${trimmed}". Please use numeric values only (e.g., 10.50)`,
      sanitised: false,
      rawValue,
    };
  }

  const parsed = Number(cleaned);
  if (!Number.isFinite(parsed)) {
    return {
      value: null,
      error: `Invalid price format: "${trimmed}". Please use numeric values only (e.g., 10.50)`,
      sanitised: false,
      rawValue,
    };
  }
  if (parsed < 0) {
    return { value: null, error: 'Price cannot be negative', sanitised: false, rawValue };
  }

  // We got here by correcting the input, so the caller should surface it.
  return { value: parsed, error: null, sanitised: true, rawValue };
}
